import { type ChangeEvent, type Dispatch, Fragment, memo, type SetStateAction } from "react";
import "Components/ConfigLayout/Camera/AiAnalytics/AiRuleSettings/AiRuleSettings.less";
import {
	AspectMaintainer, Button, ButtonTypes, Checkbox, Icon, IconSize, IconWeight, Input, InteractiveSvg,
	type SelectOption, Popover, RangeSlider, Select, Switch
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { AiRule, AiAnalyticsState } from "Interfaces";
import { Link } from "react-router-dom";
import { AiRuleDirection, AiRuleType } from "Data/Objects/Camera";
import type { Point } from "@clintonelec/react-storybook";
import { constant, times } from "lodash";

const ruleTypeOptions: SelectOption[] = Object.values(AiRuleType).map((type) => ({
	label: type,
	value: type
}));

const directionOptions: SelectOption[] = Object.values(AiRuleDirection).map((direction) => ({
	label: direction,
	value: direction
}));

type AiRuleSettingsProps = {
	localAiAnalyticsState: [ AiAnalyticsState, Dispatch<SetStateAction<AiAnalyticsState>> ];
	selectedRuleIndex: number;
	setSelectedRuleIndex: Dispatch<SetStateAction<number>>;
};

const defaultLinePoints = [
	{
		x: 960,
		y: 340
	},
	{
		x: 960,
		y: 740
	}
];

const defaultZonePoints = [
	{ x: 760, y: 340 },
	{ x: 1160, y: 340 },
	{ x: 1160, y: 740 },
	{ x: 760, y: 740 }
];

function AiRuleSettings(props: AiRuleSettingsProps) {
	const { localAiAnalyticsState, selectedRuleIndex, setSelectedRuleIndex } = props;
	const [ localAiAnalyticsSettings, setLocalAiAnalyticsSettings ] = localAiAnalyticsState;
	const { rules, enabled } = localAiAnalyticsSettings;
	const disabledOption: SelectOption = { label: "Disabled", value: -1 };
	const createdRules = rules.filter(rule => !!rule);
	const counterOptions: SelectOption[] = [ disabledOption ].concat(createdRules.map((rule, index) => ({
		label: `Rule ${ index + 1 } - ${ rule?.type }`,
		value: index
	})));

	const link = (
		<div>
			Go to <Link to="/setup/system/schedule">Schedule Preset</Link> to create a new preset.
		</div>
	);

	const handleScheduleChange = (scheduleName: string) => (event: ChangeEvent<HTMLInputElement>) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].schedule[ scheduleName ] = event.currentTarget.checked;
		}));
	};

	const handleRuleTypeSelect = (type: string | null) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!type || !draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].type = type as AiRuleType;

			switch (draft.rules[ selectedRuleIndex ].type) {
				case AiRuleType.ZONE:
					draft.rules[ selectedRuleIndex ].points = defaultZonePoints;

					break;
				case AiRuleType.LINE:
					draft.rules[ selectedRuleIndex ].points = defaultLinePoints;
					draft.rules[ selectedRuleIndex ].direction = AiRuleDirection.BOTH;

					break;
			}
		}));
	};

	const handlePointsChange = (points: Point[]) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].points = points;
		}));
	};

	const handleCounterPointsChange = (point: Point) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].counter.point = point;
		}));
	};

	const handleHumanChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].objectTypes.human = event.currentTarget.checked;
		}));
	};

	const handleVehicleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].objectTypes.vehicle = event.currentTarget.checked;
		}));
	};

	const handleBikeChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].objectTypes.bike = event.currentTarget.checked;
		}));
	};

	const handleAudioFileSelect = (filename: string | null) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].audioFile = filename ?? "";
		}));
	};

	const handleConfidenceThresholdChange = (confidenceThreshold: number | number[]) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (Array.isArray(confidenceThreshold) || !draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].confidenceThreshold = confidenceThreshold;
		}));
	};

	const handleDirectionSelect = (direction: string | null) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!direction || draft.rules[ selectedRuleIndex ]?.type !== AiRuleType.LINE) {
				return;
			}

			draft.rules[ selectedRuleIndex ].direction = direction as AiRuleDirection;
		}));
	};

	const handleClearActiveRule = () => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.rules[ selectedRuleIndex ] = null;
		}));

		setSelectedRuleIndex(-1);
	};

	const handleClearAllRules = () => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.rules = times(8, (constant(null)));
		}));

		setSelectedRuleIndex(-1);
	};

	const handleIntrusionChange = (intrusion: boolean) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (draft.rules[ selectedRuleIndex ]?.type !== AiRuleType.ZONE) {
				return;
			}

			draft.rules[ selectedRuleIndex ].intrusion = intrusion;
		}));
	};

	const handleEnterChange = (enter: boolean) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (draft.rules[ selectedRuleIndex ]?.type !== AiRuleType.ZONE) {
				return;
			}

			draft.rules[ selectedRuleIndex ].enter = enter;
		}));
	};

	const handleExitChange = (exit: boolean) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (draft.rules[ selectedRuleIndex ]?.type !== AiRuleType.ZONE) {
				return;
			}

			draft.rules[ selectedRuleIndex ].exit = exit;
		}));
	};

	const handleLoiterChange = (loiter: boolean) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (draft.rules[ selectedRuleIndex ]?.type !== AiRuleType.ZONE) {
				return;
			}

			draft.rules[ selectedRuleIndex ].loiter = loiter;
		}));
	};

	const handleLoiterDurationChange = (loiterDuration: number | number[]) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (Array.isArray(loiterDuration) || draft.rules[ selectedRuleIndex ]?.type !== AiRuleType.ZONE) {
				return;
			}

			draft.rules[ selectedRuleIndex ].loiterDuration = loiterDuration;
		}));
	};

	const handleCounterEnabledChange = (counterEnabled: boolean) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].counter.enabled = counterEnabled;
		}));
	};

	const handleCounterNotificationEnabledChange = (notificationEnabled: boolean) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].counter.notificationEnabled = notificationEnabled;
		}));
	};

	const handleCounterNotificationCountSave = (notificationCount: string) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].counter.notificationCounter = +notificationCount;
		}));
	};

	const handleCounterResetChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].counter.resetCounterAfterNotification = event.currentTarget.checked;
		}));
	};

	const handleCounterIncreaseRuleSelect = (increaseRule: string | null) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!increaseRule || !draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].counter.increaseRule = +increaseRule;
		}));
	};

	const handleCounterDecreaseRuleSelect = (decreaseRule: string | null) => {
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			if (!decreaseRule || !draft.rules[ selectedRuleIndex ]) {
				return;
			}

			draft.rules[ selectedRuleIndex ].counter.decreaseRule = +decreaseRule;
		}));
	};

	const handleCreateRuleClick = (index: number) => () => {
		const rule: AiRule = {
			type: AiRuleType.ZONE,
			intrusion: true,
			enter: false,
			exit: false,
			loiter: false,
			loiterDuration: 5,
			objectTypes: {
				human: true,
				vehicle: true,
				bike: true
			},
			confidenceThreshold: 25,
			audioFile: "", // TODO: replace with actual list
			schedule: { always: true, day: true, night: true }, // TODO: replace with actual list
			points: defaultZonePoints,
			counter: {
				enabled: false,
				notificationEnabled: false,
				notificationCounter: 1,
				resetCounterAfterNotification: false,
				increaseRule: -1,
				decreaseRule: -1,
				point: { x: 1440, y: 810 },
				count: 0
			}
		};

		setSelectedRuleIndex(index);
		setLocalAiAnalyticsSettings(produce(localAiAnalyticsSettings, (draft) => {
			draft.rules[ index ] = rule;
		}));
	};

	const renderSchedule = (schedule: [ string, boolean ]) => {
		const [ scheduleName, checked ] = schedule;

		return (
			<Checkbox checked={ checked } key={ scheduleName } onChange={ handleScheduleChange(scheduleName) }>
				<span>{ scheduleName }</span>
			</Checkbox>
		);
	};

	const renderLineSettings = () => {
		if (rules[ selectedRuleIndex ]?.type !== AiRuleType.LINE) {
			return;
		}

		return (
			<div className="form-row">
				<span>Direction</span>
				<Select
					allowClear={ false }
					onSelect={ handleDirectionSelect }
					options={ directionOptions }
					value={ rules[ selectedRuleIndex ].direction }
				/>
			</div>
		);
	};

	const renderZoneSettings = () => {
		if (rules[ selectedRuleIndex ]?.type !== AiRuleType.ZONE) {
			return;
		}

		const { loiter, enter, exit, intrusion } = rules[ selectedRuleIndex ];
		let warningMessage = null;

		if (!loiter && !enter && !exit && !intrusion) {
			warningMessage = (
				<div className="warning-message">You must enable at least one of the above toggles.</div>
			);
		}

		return (
			<Fragment>
				<div className="form-row">
					<span>Intrusion</span>
					<div>
						<Switch
							checked={ rules[ selectedRuleIndex ].intrusion }
							onChange={ handleIntrusionChange }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Enter</span>
					<div>
						<Switch
							checked={ rules[ selectedRuleIndex ].enter }
							onChange={ handleEnterChange }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Exit</span>
					<div>
						<Switch
							checked={ rules[ selectedRuleIndex ].exit }
							onChange={ handleExitChange }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Loiter</span>
					<div>
						<Switch
							checked={ rules[ selectedRuleIndex ].loiter }
							onChange={ handleLoiterChange }
						/>
					</div>
				</div>
				{ warningMessage }
				<div className="form-row">
					<span>Loiter Duration (sec)</span>
					<div className="slider-container">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							min={ 5 }
							max={ 600 }
							onChange={ handleLoiterDurationChange }
							value={ rules[ selectedRuleIndex ].loiterDuration }
							disabled={ !rules[ selectedRuleIndex ].loiter }
						/>
					</div>
				</div>
			</Fragment>
		);
	};

	const renderRuleSettings = () => {
		if (!enabled) {
			return (
				<div className="add-rule-message">Enable AI Analytics to adjust rule settings.</div>
			);
		}

		if (selectedRuleIndex < 0 || !rules[selectedRuleIndex]) {
			return (
				<div className="add-rule-message">Create a rule to adjust rule settings.</div>
			);
		}

		const { human, vehicle, bike } = rules[ selectedRuleIndex ].objectTypes;
		let warningMessage = null;

		if (!human && !vehicle && !bike) {
			warningMessage = (
				<div className="warning-message">You must enable at least one object.</div>
			);
		}

		const { enabled: counterEnabled, notificationEnabled } = rules[ selectedRuleIndex ].counter;
		const counterOptionsDisabledClass = counterEnabled ? "" : " counter-options-disabled";
		const counterNotificationDisabledClass = notificationEnabled ? "" : " counter-options-disabled";
		const notificationPopoverContent = `Set the number of detected objects to trigger an event notification.
			Select Reset Counter to clear the count after an event notification.`;

		return (
			<div className="rule-settings">
				<div className="form-row">
					<span>AI Rule Type</span>
					<Select
						allowClear={ false }
						onSelect={ handleRuleTypeSelect }
						options={ ruleTypeOptions }
						value={ rules[ selectedRuleIndex ].type }
					/>
				</div>
				{ renderLineSettings() }
				{ renderZoneSettings() }
				<div className="form-row">
					<span>Object</span>
					<div className="schedules">
						<Checkbox checked={ human } onChange={ handleHumanChange }>
							<span>Human</span>
						</Checkbox>
						<Checkbox checked={ vehicle } onChange={ handleVehicleChange }>
							<span>Vehicle</span>
						</Checkbox>
						<Checkbox checked={ bike } onChange={ handleBikeChange }>
							<span>Bike</span>
						</Checkbox>
					</div>
				</div>
				{ warningMessage }
				<div className="form-row">
					<span>Confidence Threshold (%)</span>
					<div className="slider-container">
						<RangeSlider
							hideInputCharMessage
							inputDisplay
							min={ 0 }
							max={ 100 }
							onChange={ handleConfidenceThresholdChange }
							value={ rules[ selectedRuleIndex ].confidenceThreshold }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Play Audio File</span>
					<Select
						onSelect={ handleAudioFileSelect }
						value={ rules[ selectedRuleIndex ].audioFile }
					/>
				</div>
				<div className="form-row">
					<div className="schedule-title">
						<span>Schedule</span>
						<Popover content={ link }>
							<Icon name="circle-info" iconWeight={ IconWeight.LIGHT } size={ IconSize.SMALLEST } />
						</Popover>
					</div>
					<div className="schedules">
						{ Object.entries(rules[ selectedRuleIndex ].schedule).map(renderSchedule) }
					</div>
				</div>
				<div className="form-row">
					<span>Add Counter</span>
					<div>
						<Switch
							checked={ counterEnabled }
							onChange={ handleCounterEnabledChange }
						/>
					</div>
				</div>
				<div className={ "counter-options" + counterOptionsDisabledClass }>
					<div className="form-row">
						<span>
							<span>Event Notification Count</span>
							<Popover content={ notificationPopoverContent }>
								<Icon name="circle-info" iconWeight={ IconWeight.LIGHT } size={ IconSize.SMALLEST } />
							</Popover>
						</span>
						<div className="counter-notification-settings">
							<Switch
								checked={ rules[ selectedRuleIndex ].counter.notificationEnabled }
								onChange={ handleCounterNotificationEnabledChange }
								disabled={ !counterEnabled }
							/>
							<Input
								type="number"
								min={ 1 }
								max={ 32767 }
								value={ rules[ selectedRuleIndex ].counter.notificationCounter }
								onSave={ handleCounterNotificationCountSave }
								disabled={ !counterEnabled || !notificationEnabled }
								className={ counterNotificationDisabledClass }
								hideCharMessage
							/>
							<div className={ "checkbox-wrapper" + counterNotificationDisabledClass }>
								<Checkbox
									checked={ rules[ selectedRuleIndex ].counter.resetCounterAfterNotification }
									id="resetCounter"
									onChange={ handleCounterResetChange }
									disabled={ !counterEnabled || !notificationEnabled }
								/>
								<label htmlFor="resetCounter">Reset Counter</label>
							</div>
						</div>
					</div>
					<div className="form-row">
						<span>Counter Increase</span>
						<Select
							allowClear={ false }
							onSelect={ handleCounterIncreaseRuleSelect }
							options={ counterOptions }
							value={ rules[ selectedRuleIndex ].counter.increaseRule }
						/>
					</div>
					<div className="form-row">
						<span>Counter Decrease</span>
						<Select
							allowClear={ false }
							onSelect={ handleCounterDecreaseRuleSelect }
							options={ counterOptions }
							value={ rules[ selectedRuleIndex ].counter.decreaseRule }
						/>
					</div>
				</div>
			</div>
		);
	};

	const handleRuleButtonClick = (index: number) => () => {
		const newIndex = selectedRuleIndex === index ? -1 : index;

		setSelectedRuleIndex(newIndex);

		if(newIndex !== null && localAiAnalyticsSettings.rules[ newIndex ]?.points.length === 0) {
			const newLocalState = produce(localAiAnalyticsSettings, draft => {
				if (draft.rules[ newIndex ]?.points.length === 0) {
					draft.rules[ newIndex ].points = defaultZonePoints;
				}
			});

			setLocalAiAnalyticsSettings(newLocalState);
		}
	};

	const renderRuleButtons = () => times(8, index => {
		if (!rules[ index ]) {
			return (
				<Button
					key={ index }
					type={ ButtonTypes.SECONDARY }
					ghost={ index !== selectedRuleIndex }
					onClick={ handleCreateRuleClick(index) }
					htmlType="button"
					disabled={ !enabled }
				>
					+
				</Button>
			);
		}

		return (
			<Button
				key={ index }
				type={ index !== selectedRuleIndex ? ButtonTypes.SECONDARY : ButtonTypes.TERTIARY }
				ghost={ index !== selectedRuleIndex }
				onClick={ handleRuleButtonClick(index) }
				htmlType="button"
				disabled={ !enabled }
			>
				{ `Rule ${ index + 1 }` }
			</Button>
		);
	});

	const renderCounter = () => {
		const counter = rules[ selectedRuleIndex ]?.counter;

		if (!counter?.enabled) {
			return null;
		}

		const { point: counterPoint, count } = counter;

		return (
			<InteractiveSvg.Text onChange={ handleCounterPointsChange } point={ counterPoint }>
				{ count }
			</InteractiveSvg.Text>
		);
	};

	const renderInteractiveElement = () => {
		if (selectedRuleIndex < 0 || !enabled || !rules[ selectedRuleIndex ]) {
			return;
		}

		const { type, points } = rules[ selectedRuleIndex ];

		if (type === AiRuleType.ZONE) {
			return (
				<InteractiveSvg.Polygon
					key={ `zone-${ selectedRuleIndex }` }
					points={ points }
					onChange={ handlePointsChange }
				/>
			);
		}

		const { direction } = rules[ selectedRuleIndex ];

		return (
			<InteractiveSvg.Line
				arrowA={ direction === AiRuleDirection.A || direction === AiRuleDirection.BOTH }
				arrowB={ direction === AiRuleDirection.B || direction === AiRuleDirection.BOTH }
				points={ [ points[0], points[1] ] }
				onChange={ handlePointsChange } />
		);
	};

	return (
		<div className="ai-rule-settings two-column-card-content">
			<div className="column left-content">
				<div className="camera-container">
					<AspectMaintainer>
						<div className="camera-snapshot">
							<InteractiveSvg>
								{ renderInteractiveElement() }
								{ renderCounter() }
							</InteractiveSvg>
						</div>
					</AspectMaintainer>
				</div>
				<div className="rule-buttons">
					{ renderRuleButtons() }
				</div>
				<div className="controls-container">
					<Button
						className="rule-action-button"
						disabled={ !enabled || selectedRuleIndex === -1 }
						ghost
						htmlType="button"
						onClick={ handleClearActiveRule }
						type={ ButtonTypes.SECONDARY }
					>
						Clear Active Rule
					</Button>
					<Button
						className="rule-action-button"
						disabled={ !enabled || !rules.some((rule) => !!rule) }
						type={ ButtonTypes.SECONDARY }
						ghost
						htmlType="button"
						onClick={ handleClearAllRules }
					>
						Clear All Rules
					</Button>
				</div>
			</div>
			<div className="column right-content">
				{ renderRuleSettings() }
			</div>
		</div>
	);
}

export default memo(AiRuleSettings);
